import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FaClock,
  FaUsers,
  FaShieldAlt,
  FaCogs,
  FaMobile,
  FaDatabase,
  FaNetworkWired,
  FaSearch,
  FaServer,
  FaEye,
  FaLock,
  FaBolt
} from 'react-icons/fa';
import { ink, inkMid, sky, paper, paperMuted, borderInk, borderInkHover, bodyMuted } from '../theme';
import BlueprintSectionTitle from './BlueprintSectionTitle';

const ProblemsSection = styled.section`
  padding: 100px 0;
  background: ${paper};
  position: relative;
  font-family: 'Inter', system-ui, sans-serif;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const SectionSubtitle = styled.p`
  font-size: 1.05rem;
  text-align: center;
  margin-bottom: 3rem;
  color: ${bodyMuted};
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2.5rem;
    padding: 0 10px;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin-bottom: 2rem;
    padding: 0 5px;
  }

  @media (max-width: 360px) {
    font-size: 0.9rem;
    margin-bottom: 1.75rem;
    line-height: 1.5;
  }
`;

/** Липкая панель табов: прилипает под фиксированный Header до конца секции */
const StickyTabsRail = styled.div`
  position: sticky;
  top: 80px;
  z-index: 200;
  width: calc(100% + 40px);
  max-width: none;
  margin-left: -20px;
  margin-right: -20px;
  margin-bottom: 2.5rem;
  box-sizing: border-box;
  padding: 0.65rem 20px 0.85rem;
  background: ${paper};
  border-bottom: 1px solid ${borderInk};
  box-shadow: 0 6px 18px rgba(3, 26, 56, 0.06);

  @media (max-width: 768px) {
    margin-bottom: 2rem;
    padding: 0.55rem 16px 0.75rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 1.75rem;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 10px;
  }

  @media (max-width: 480px) {
    gap: 8px;
    flex-direction: column;
    align-items: center;
  }

  @media (max-width: 360px) {
    gap: 6px;
  }
`;

const Tab = styled.button`
  background: ${props => (props.active ? ink : paperMuted)};
  border: 1px solid ${props => (props.active ? 'rgba(142, 207, 255, 0.45)' : borderInk)};
  border-left: 3px solid ${props => (props.active ? sky : borderInk)};
  color: ${props => (props.active ? '#f8fbff' : bodyMuted)};
  padding: 12px 25px;
  cursor: pointer;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease,
    box-shadow 0.2s ease;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: ${props => (props.active ? '0 6px 20px rgba(3, 26, 56, 0.18)' : 'none')};
  border-radius: 2px;

  svg {
    color: ${props => (props.active ? sky : inkMid)};
  }

  &:hover {
    background: ${props => (props.active ? ink : paper)};
    border-color: ${borderInkHover};
    color: ${props => (props.active ? '#fff' : ink)};
    box-shadow: 0 4px 14px rgba(3, 26, 56, 0.08);
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 0.78rem;
  }

  @media (max-width: 480px) {
    padding: 10px 18px;
    font-size: 0.75rem;
    width: 100%;
    max-width: 280px;
    justify-content: center;
  }

  @media (max-width: 360px) {
    padding: 8px 15px;
    font-size: 0.72rem;
    max-width: 260px;
  }
`;

const ContentArea = styled(motion.div)`
  min-height: 500px;
`;

// ===== ПРОБЛЕМЫ ИНТЕГРАЦИИ =====
const IntegrationProblems = styled.div`
  background: ${paper};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  padding: 40px;
  font-family: 'Inter', system-ui, sans-serif;
  position: relative;
  box-shadow: 0 4px 16px rgba(3, 26, 56, 0.05);
`;

const ProblemsHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const ProblemsTitle = styled.h3`
  font-size: 2.2rem;
  color: ${inkMid};
  margin-bottom: 15px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 1.9rem;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 1.7rem;
    margin-bottom: 10px;
  }

  @media (max-width: 360px) {
    font-size: 1.5rem;
    margin-bottom: 8px;
  }
`;

const ProblemsSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${bodyMuted};
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 10px;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    padding: 0 5px;
  }

  @media (max-width: 360px) {
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const ProblemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 25px;
  }

  @media (max-width: 480px) {
    gap: 20px;
  }

  @media (max-width: 360px) {
    gap: 15px;
  }
`;

const ProblemCard = styled(motion.div)`
  background: ${paperMuted};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  padding: 25px;
  transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
  position: relative;
  font-family: 'Inter', system-ui, sans-serif;
  box-shadow: 0 2px 10px rgba(3, 26, 56, 0.04);

  &:hover {
    border-color: ${borderInkHover};
    box-shadow: 0 8px 22px rgba(3, 26, 56, 0.08);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 20px 15px;
  }

  @media (max-width: 480px) {
    padding: 18px 12px;
  }

  @media (max-width: 360px) {
    padding: 15px 10px;
  }
`;

const ProblemIcon = styled.div`
  width: 50px;
  height: 50px;
  background: rgba(142, 207, 255, 0.12);
  border: 1px solid ${borderInk};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  font-size: 1.3rem;
  color: ${inkMid};
  font-family: 'Inter', system-ui, sans-serif;

  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    font-size: 1.2rem;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    font-size: 1.1rem;
    margin-bottom: 10px;
  }

  @media (max-width: 360px) {
    width: 35px;
    height: 35px;
    font-size: 1rem;
    margin-bottom: 8px;
  }
`;

const ProblemTitle = styled.h4`
  font-size: 1.2rem;
  color: ${ink};
  margin-bottom: 15px;
  font-weight: 700;
  font-family: 'Inter', system-ui, sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.06em;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 10px;
  }

  @media (max-width: 360px) {
    font-size: 0.95rem;
    margin-bottom: 8px;
  }
`;

const ProblemDescription = styled.p`
  color: ${bodyMuted};
  line-height: 1.6;
  margin-bottom: 20px;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 0.9rem;

  @media (max-width: 768px) {
    font-size: 0.85rem;
    line-height: 1.5;
    margin-bottom: 18px;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
    line-height: 1.4;
    margin-bottom: 15px;
  }

  @media (max-width: 360px) {
    font-size: 0.75rem;
    line-height: 1.3;
    margin-bottom: 12px;
  }
`;

const SolutionPreview = styled.div`
  background: ${paper};
  border: 1px solid ${borderInk};
  border-left: 3px solid ${sky};
  padding: 15px;
  margin-top: 15px;
  font-family: 'Inter', system-ui, sans-serif;

  @media (max-width: 768px) {
    padding: 12px;
    margin-top: 12px;
  }

  @media (max-width: 480px) {
    padding: 10px;
    margin-top: 10px;
  }

  @media (max-width: 360px) {
    padding: 8px;
    margin-top: 8px;
  }
`;

const SolutionTitle = styled.h5`
  color: ${inkMid};
  font-size: 0.85rem;
  margin-bottom: 8px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-family: 'Inter', system-ui, sans-serif;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 6px;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    margin-bottom: 5px;
  }

  @media (max-width: 360px) {
    font-size: 0.7rem;
    margin-bottom: 4px;
  }
`;

const SolutionText = styled.p`
  color: ${bodyMuted};
  font-size: 0.85rem;
  line-height: 1.5;
  font-family: 'Inter', system-ui, sans-serif;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    line-height: 1.4;
  }

  @media (max-width: 480px) {
    font-size: 0.75rem;
    line-height: 1.3;
  }

  @media (max-width: 360px) {
    font-size: 0.7rem;
    line-height: 1.2;
  }
`;

// ===== ПРОБЛЕМЫ УПРАВЛЕНИЯ =====
const ManagementProblems = styled.div`
  background: ${paper};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  padding: 40px;
  font-family: 'Inter', system-ui, sans-serif;
  position: relative;
  box-shadow: 0 4px 16px rgba(3, 26, 56, 0.05);
`;

const ManagementHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const ManagementTitle = styled.h3`
  font-size: 2.2rem;
  color: ${inkMid};
  margin-bottom: 15px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 1.9rem;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 1.7rem;
    margin-bottom: 10px;
  }

  @media (max-width: 360px) {
    font-size: 1.5rem;
    margin-bottom: 8px;
  }
`;

const ManagementSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${bodyMuted};
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 10px;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    padding: 0 5px;
  }

  @media (max-width: 360px) {
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const ManagementGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
`;

// ===== ПРОБЛЕМЫ БЕЗОПАСНОСТИ =====
const SecurityProblems = styled.div`
  background: ${paper};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  padding: 40px;
  font-family: 'Inter', system-ui, sans-serif;
  position: relative;
  box-shadow: 0 4px 16px rgba(3, 26, 56, 0.05);
`;

const SecurityHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const SecurityTitle = styled.h3`
  font-size: 2.2rem;
  color: ${inkMid};
  margin-bottom: 15px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 1.9rem;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 1.7rem;
    margin-bottom: 10px;
  }

  @media (max-width: 360px) {
    font-size: 1.5rem;
    margin-bottom: 8px;
  }
`;

const SecuritySubtitle = styled.p`
  font-size: 1.1rem;
  color: ${bodyMuted};
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 10px;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    padding: 0 5px;
  }

  @media (max-width: 360px) {
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const SecurityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
`;

// ===== ПРОБЛЕМЫ МОНИТОРИНГА =====
const MonitoringProblems = styled.div`
  background: ${paper};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  padding: 40px;
  font-family: 'Inter', system-ui, sans-serif;
  position: relative;
  box-shadow: 0 4px 16px rgba(3, 26, 56, 0.05);
`;

const MonitoringHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const MonitoringTitle = styled.h3`
  font-size: 2.2rem;
  color: ${inkMid};
  margin-bottom: 15px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 1.9rem;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 1.7rem;
    margin-bottom: 10px;
  }

  @media (max-width: 360px) {
    font-size: 1.5rem;
    margin-bottom: 8px;
  }
`;

const MonitoringSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${bodyMuted};
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 10px;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    padding: 0 5px;
  }

  @media (max-width: 360px) {
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const MonitoringGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
`;

// ===== ПРОБЛЕМЫ ПРОИЗВОДИТЕЛЬНОСТИ =====
const PerformanceProblems = styled.div`
  background: ${paper};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  padding: 40px;
  font-family: 'Inter', system-ui, sans-serif;
  position: relative;
  box-shadow: 0 4px 16px rgba(3, 26, 56, 0.05);
`;

const PerformanceHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const PerformanceTitle = styled.h3`
  font-size: 2.2rem;
  color: ${inkMid};
  margin-bottom: 15px;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 1.9rem;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 1.7rem;
    margin-bottom: 10px;
  }

  @media (max-width: 360px) {
    font-size: 1.5rem;
    margin-bottom: 8px;
  }
`;

const PerformanceSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${bodyMuted};
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0 10px;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    padding: 0 5px;
  }

  @media (max-width: 360px) {
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const PerformanceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
`;

const Problems = () => {
  const [activeTab, setActiveTab] = useState('integration');

  const integrationProblems = [
    {
      icon: <FaServer />,
      title: "Разнородные платформы усложняют управление безопасностью",
      description: "Применение разных гипервизоров, средств контейнеризации и оркестрации в пределах одной инфраструктуры (VMware, Proxmox, Docker, Kubernetes), а также физических серверов, требует индивидуальных подходов к настройке безопасности и управлению изменениями. Это увеличивает затраты времени, риски возникновения ошибок и, как следсвтие, ведет к существенному снижению уровня защищенности и устойчивости всей инфраструктуры",
      solution: "Централизованное управление безопасностью за счет интеграции с широким спектром платформ, снижение затрат при эксплуатации, внесении изменений, развертывании новых сегментов, миграции"
    },
    {
      icon: <FaEye />,
      title: "Отсутствие единого центра управления политиками ИБ для разных типов элементов инфраструктуры",
      description: "Как правило, в современных организациях присутствует ряд аппаратных и программных решений, не связанных друг с другом в части управления и контроля за политиками",
      solution: "Замена отдельных распределенных решений на единое для детального понимания состояния ИБ в сегментах сети и управления ею. Формирование эффективного инструмента для офицеров и инженеров безопасности"
    }
  ];

  const managementProblems = [
    {
      icon: <FaCogs />,
      title: "Неэффективное управление политиками ИБ",
      description: "Специалисты вручную создают правила безопасности, которые зачастую избыточны или дублируют друг друга. Со временем правила накапливаются, появляются 'фантомные' (неиспользуемые) правила, которые никто не удаляет из-за страха нарушить работу системы",
      solution: "Автоматическая генерация правил на основе политик и связей позволяют создавать только необходимые правила, устранять дубли и удалять неиспользуемые, тем самым упрощается управление и повышается безопасность"
    },
    {
      icon: <FaClock />,
      title: "Высокая сложность ручного управления правилами безопасности",
      description: "Ручная настройка множества правил для каждого сервера, ВМ или контейнера занимает время и приводит к ошибкам",
      solution: "Автоматическая генерация правил на основе политик и связей между объектами упрощает управление и минимизирует человеческий фактор"
    },
    {
      icon: <FaUsers />,
      title: "Ручное управление на уровне пользователей приводит к ошибкам и задержкам",
      description: "Назначение прав доступа вручную для пользователей системы требует значительных усилий и увеличивает риск неправильных настроек",
      solution: "Ролевое управление доступом автоматизирует процесс, обеспечивая точное распределение прав и ускоряя администрирование"
    },
    {
      icon: <FaNetworkWired />,
      title: "Сложности масштабирования инфраструктуры безопасности",
      description: "При росте числа серверов, ВМ или контейнеров традиционные системы безопасности не справляются с нагрузкой",
      solution: "Распределенная архитектура МСЭ системы распределяет функции управления и контроля между узлами, тем самым обеспечивается масштабируемость без потери производительности"
    }
  ];

  const securityProblems = [
    {
      icon: <FaShieldAlt />,
      title: "Избыточное количество правил создает уязвимости",
      description: "Большое число правил безопасности усложняет их аудит и приводит к ошибочным разрешениям",
      solution: "Анализ и оптимизация правил в системе выявляет избыточные разрешения, снижает риски компрометации"
    },
    {
      icon: <FaLock />,
      title: "Ограниченное число VLAN затрудняет сегментацию сети",
      description: "Лимит доступных VLAN ограничивает возможности разделения трафика, особенно в крупных инфраструктурах",
      solution: "Сегментация трафика, даже в одном VLAN, позволяет создавать виртуальные сегменты, обеспечивает контроль объектов и эффективное использование сетевых ресурсов"
    },
    {
      icon: <FaDatabase />,
      title: "Недостаточная изоляция приложений увеличивает риск утечек",
      description: "Приложения, работающие на одном сервере или в одном VLAN, могут взаимодействовать напрямую, без средств контроля",
      solution: "Сегментация приложений изолирует их трафик, предотвращает утечки данных и несанкционированный доступ"
    },
    {
      icon: <FaMobile />,
      title: "Уязвимости микросервисов из-за их динамичности",
      description: "Микросервисы зачастую создаются и удаляются автоматически. Это затрудняет контроль за их безопасностью",
      solution: "Поддержка микросервисной архитектуры обеспечивает динамическое применение политик, защищает микросервисы в реальном времени"
    }
  ];

  const monitoringProblems = [
    {
      icon: <FaEye />,
      title: "Отсутствие полной видимости трафика и событий в сети",
      description: "Без мониторинга трафика и событий невозможно оперативно выявлять угрозы",
      solution: "Мониторинг и аналитика в реальном времени предоставляют полную картину активности сети, позволяет оперативно реагировать на инциденты"
    },
    {
      icon: <FaSearch />,
      title: "Непроверенные политики повышают риск возникновения уязвимостей",
      description: "Применение политик без тестирования несет риск возникновения ошибок",
      solution: "Моделирование атак позволяет тестировать политики в безопасной среде, выявлять слабые места до возникновения реальных инцидентов"
    }
  ];

  const performanceProblems = [
    {
      icon: <FaServer />,
      title: "Риски при миграции виртуальных машин",
      description: "Перемещение ВМ между хостами может нарушить политики безопасности или вызвать сбои",
      solution: "Контроль миграции ВМ со стороны Системы обеспечивает применение политик в новой среде"
    },
    {
      icon: <FaNetworkWired />,
      title: "Перегрузка сетевых инженеров задачами ИБ",
      description: "Сетевые инженеры вынуждены заниматься настройкой политик ИБ, что отвлекает от их основных задач",
      solution: "Перенос функций управления политиками ИБ на специалистов по безопасности позволяет сетевым инженерам сосредоточиться на управлении сетевыми функциями"
    },
    {
      icon: <FaBolt />,
      title: "Медленное реагирование на инциденты увеличивает ущерб",
      description: "Ручное реагирование на угрозы замедляет изоляцию скомпрометированных объектов и устранение угроз",
      solution: "Автоматизация реагирования, например, изоляция узлов или блокировка трафика, минимизирует время реакции и ущерб от атаки"
    },
    {
      icon: <FaShieldAlt />,
      title: "Низкая надежность из-за зависимости от аппаратных МСЭ",
      description: "Сбой одного аппаратного МСЭ может нарушить работу всей сети",
      solution: "Распределение функций МСЭ между агентами, контролирующими ВМ и контейнеры, повышает надежность инфраструктуры"
    }
  ];

  return (
    <ProblemsSection id="problems">
      <Container>
        <BlueprintSectionTitle>Проблемы, решаемые e-node оазис (cloud)</BlueprintSectionTitle>
        <SectionSubtitle>
          Система e-node оазис (cloud) предназначена для устранения или минимизации типовых проблем, возникающих при эксплуатации крупной распределенной ИТ-инфраструктуры
        </SectionSubtitle>

        <StickyTabsRail>
          <TabsContainer>
            <Tab
              active={activeTab === 'integration'}
              onClick={() => setActiveTab('integration')}
            >
              <FaServer /> Интеграция
            </Tab>
            <Tab
              active={activeTab === 'management'}
              onClick={() => setActiveTab('management')}
            >
              <FaCogs /> Управление
            </Tab>
            <Tab
              active={activeTab === 'security'}
              onClick={() => setActiveTab('security')}
            >
              <FaShieldAlt /> Безопасность
            </Tab>
            <Tab
              active={activeTab === 'monitoring'}
              onClick={() => setActiveTab('monitoring')}
            >
              <FaEye /> Мониторинг
            </Tab>
            <Tab
              active={activeTab === 'performance'}
              onClick={() => setActiveTab('performance')}
            >
              <FaBolt /> Производительность
            </Tab>
          </TabsContainer>
        </StickyTabsRail>

        <ContentArea
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {activeTab === 'integration' && (
            <IntegrationProblems>
              <ProblemsHeader>
                <ProblemsTitle>Проблемы интеграции и совместимости</ProblemsTitle>
                <ProblemsSubtitle>
                  Вызовы, связанные с разнородностью платформ и отсутствием интеграции 
                  с внешними системами безопасности
                </ProblemsSubtitle>
              </ProblemsHeader>
              
              <ProblemsGrid>
                {integrationProblems.map((problem, index) => (
                  <ProblemCard
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <ProblemIcon>{problem.icon}</ProblemIcon>
                    <ProblemTitle>{problem.title}</ProblemTitle>
                    <ProblemDescription>{problem.description}</ProblemDescription>
                    <SolutionPreview>
                      <SolutionTitle>💡 Решение e-node оазис (cloud):</SolutionTitle>
                      <SolutionText>{problem.solution}</SolutionText>
                    </SolutionPreview>
                  </ProblemCard>
                ))}
              </ProblemsGrid>
            </IntegrationProblems>
          )}

          {activeTab === 'management' && (
            <ManagementProblems>
              <ManagementHeader>
                <ManagementTitle>Проблемы управления и автоматизации</ManagementTitle>
                <ManagementSubtitle>
                  Сложности ручного управления политиками безопасности, недостаток автоматизации 
                  и проблемы масштабирования
                </ManagementSubtitle>
              </ManagementHeader>
              
              <ManagementGrid>
                {managementProblems.map((problem, index) => (
                  <ProblemCard
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <ProblemIcon>{problem.icon}</ProblemIcon>
                    <ProblemTitle>{problem.title}</ProblemTitle>
                    <ProblemDescription>{problem.description}</ProblemDescription>
                    <SolutionPreview>
                      <SolutionTitle>💡 Решение e-node оазис (cloud):</SolutionTitle>
                      <SolutionText>{problem.solution}</SolutionText>
                    </SolutionPreview>
                  </ProblemCard>
                ))}
              </ManagementGrid>
            </ManagementProblems>
          )}

          {activeTab === 'security' && (
            <SecurityProblems>
              <SecurityHeader>
                <SecurityTitle>Проблемы безопасности и сегментации</SecurityTitle>
                <SecuritySubtitle>
                  Уязвимости из-за избыточных правил, ограничений VLAN и недостаточной 
                  изоляции приложений
                </SecuritySubtitle>
              </SecurityHeader>
              
              <SecurityGrid>
                {securityProblems.map((problem, index) => (
                  <ProblemCard
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <ProblemIcon>{problem.icon}</ProblemIcon>
                    <ProblemTitle>{problem.title}</ProblemTitle>
                    <ProblemDescription>{problem.description}</ProblemDescription>
                    <SolutionPreview>
                      <SolutionTitle>💡 Решение e-node оазис (cloud):</SolutionTitle>
                      <SolutionText>{problem.solution}</SolutionText>
                    </SolutionPreview>
                  </ProblemCard>
                ))}
              </SecurityGrid>
            </SecurityProblems>
          )}

          {activeTab === 'monitoring' && (
            <MonitoringProblems>
              <MonitoringHeader>
                <MonitoringTitle>Проблемы мониторинга и аналитики</MonitoringTitle>
                <MonitoringSubtitle>
                  Отсутствие полной видимости сети и риски применения непроверенных политик безопасности
                </MonitoringSubtitle>
              </MonitoringHeader>
              
              <MonitoringGrid>
                {monitoringProblems.map((problem, index) => (
                  <ProblemCard
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <ProblemIcon>{problem.icon}</ProblemIcon>
                    <ProblemTitle>{problem.title}</ProblemTitle>
                    <ProblemDescription>{problem.description}</ProblemDescription>
                    <SolutionPreview>
                      <SolutionTitle>💡 Решение e-node оазис (cloud):</SolutionTitle>
                      <SolutionText>{problem.solution}</SolutionText>
                    </SolutionPreview>
                  </ProblemCard>
                ))}
              </MonitoringGrid>
            </MonitoringProblems>
          )}

          {activeTab === 'performance' && (
            <PerformanceProblems>
              <PerformanceHeader>
                <PerformanceTitle>Проблемы производительности и надежности</PerformanceTitle>
                <PerformanceSubtitle>
                  Риски при миграции ВМ, перегрузка специалистов и зависимость от аппаратных решений
                </PerformanceSubtitle>
              </PerformanceHeader>
              
              <PerformanceGrid>
                {performanceProblems.map((problem, index) => (
                  <ProblemCard
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <ProblemIcon>{problem.icon}</ProblemIcon>
                    <ProblemTitle>{problem.title}</ProblemTitle>
                    <ProblemDescription>{problem.description}</ProblemDescription>
                    <SolutionPreview>
                      <SolutionTitle>💡 Решение e-node оазис (cloud):</SolutionTitle>
                      <SolutionText>{problem.solution}</SolutionText>
                    </SolutionPreview>
                  </ProblemCard>
                ))}
              </PerformanceGrid>
            </PerformanceProblems>
          )}
        </ContentArea>
      </Container>
    </ProblemsSection>
  );
};

export default Problems;
